import {useCustomMutation} from "@/global/hooks/useCustomMutation";
import type {
    DrivingAvailableTimesDayDto,
    DrivingScheduleMonthDto,
    OrganizationSearchDto,
    SearchOrganizationsQueryParams
} from "@/modules/exam-applications/shared";
import axios from "axios";
import {ApplicationKeys} from "@/modules/exam-applications/shared/services/application.keys";
import {useCustomQuery} from "@/global/hooks/useCustomQuery";
import {CONSTANTS} from "@/global/constants";
import type {OmmitedUseQueryOptions} from "@/global/types";
import type {
    SchoolsControllerGetOrganizationsQueryParams
} from "@/global/api/gen/ts/schoolsDTO/SchoolsControllerGetOrganizations";
import type {SchoolToOrganizationSearchDto} from "@/global/api/gen/ts/SchoolToOrganizationSearchDto";
import {SchoolKeys} from "@/modules/schools/services/school.keys";
import type {ApplicationAiTestCreateDto} from "@/global/api/gen/ts/ApplicationAiTestCreateDto";
import {useMemo, useRef} from "react";
import dayjs from "dayjs";
import {applicationAITestService, applicationPddTestService, schoolsService} from "@/global/api/gen";
import type {ApplicationPddTestCreateDto} from "@/global/api/gen/ts/ApplicationPddTestCreateDto";

const axiosClientExam = axios.create({
    withCredentials: false,
    baseURL: CONSTANTS.EXAM_URL,
    timeout: 30000,
    paramsSerializer: {
        indexes: null
    }
});

export const apiCreateApplication = () => {
    return useCustomMutation<unknown, ApplicationAiTestCreateDto>({
        queryKey: ApplicationKeys.all,
        mutationFn: (data) => applicationAITestService.applicationAiTestControllerCreate(data)
            .then(r=> r.data),
    });
};

export const apiDrivingOrganizationServiceSearch = (organization_id: number | undefined | null) => {
    return useCustomQuery({
        enabled: !!organization_id,
        queryKey: ApplicationKeys.servicesSearch(organization_id),
        queryFn: () => axiosClientExam.get(`/driving/organization/${organization_id}/services`)
            .then(r => r.data)
    });
};

export const apiSearchOrganizationPublic = (school_id: number | null, params: SchoolsControllerGetOrganizationsQueryParams, options?: OmmitedUseQueryOptions<SchoolToOrganizationSearchDto>) => {
    return useCustomQuery({
        enabled: !!school_id,
        queryKey: SchoolKeys.organizationSearch(school_id, params),
        queryFn: () => schoolsService.schoolsControllerGetOrganizations({schoolId: +(school_id ?? 0)}, params)
            .then(r => r.data),
        ...options
    });
};

export const apiSearchOrganizationAll = (params: SearchOrganizationsQueryParams, options?: OmmitedUseQueryOptions<OrganizationSearchDto>) => {
    return useCustomQuery({
        queryKey: ApplicationKeys.organizationSearch(params),
        queryFn: () => axiosClientExam.get<OrganizationSearchDto>(`/organizations/search/public`, {params})
            .then(r => r.data),
        ...options
    });
};

export const apiDrivingOrganizationSchedulesDayFree = ({ service_id, date }: {service_id: number | null | undefined, date: string | null | undefined}) => {
    return useCustomQuery({
        enabled: !!service_id && !!date,
        queryKey: ApplicationKeys.scheduleDay({service_id, date}),
        queryFn: () => axiosClientExam.get<DrivingAvailableTimesDayDto>(`/driving/services/${service_id}/times/${date}/day`)
            .then(r => r.data),
    });
};

export const apiDrivingOrganizationSchedulesDayMonth = ({ organizationId, date }: {organizationId: number | null | undefined, date: string | null | undefined}, options?: OmmitedUseQueryOptions<DrivingScheduleMonthDto>) => {
    const monthes = useRef(new Map());

    const formattedDate = useMemo(() => {
        if (!date) {
            return null;
        }
        const month = dayjs(date).format('YYYY-MM');
        if (monthes.current.has(month)) {
            return monthes.current.get(month);
        }
        monthes.current.set(month, date);
        return date;
    }, [date]);

    return useCustomQuery({
        enabled: !!organizationId && !!formattedDate,
        queryKey: ApplicationKeys.scheduleMonth({organizationId, date: formattedDate}),
        queryFn: () => axiosClientExam.get<DrivingScheduleMonthDto>(`/driving/staff/${organizationId}/schedules/${formattedDate}/month`)
            .then(r => r.data),
        ...options
    });
};

export const apiStudentAiTestByIin = () => {
    return useCustomMutation({
        mutationFn: (iin: string) => applicationAITestService.applicationAiTestControllerGetSchoolsAiTestByIin({iin: iin ?? ''})
            .then(r => r.data),
        queryKey: ApplicationKeys.all,
    });
};

export const apiCreateOnlineAitestTransaction = () => {
    return useCustomMutation({
        mutationFn: (data: ApplicationPddTestCreateDto) => applicationPddTestService.applicationPddTestControllerCreateApplication(data)
            .then((res) => res.data),
        queryKey: ApplicationKeys.all
    });
};

export const apiStartPddTestExam = () => {
    return useCustomMutation({
        mutationFn: ({id}: {id: number} ) => applicationPddTestService.applicationPddTestControllerStartTest({id})
            .then((res) => res.data),
        queryKey: ApplicationKeys.all
    });
};